<template>
  <main
    class="page"
    role="main"
    aria-labelledby="auto-offers-h1"
  >
    <header class="page-header">
      <h1
        id="auto-offers-h1"
        class="h1"
      >
        Angebote – Automatik
      </h1>
      <div class="actions">
        <button
          class="btn btn-primary"
          @click="openCreate"
        >
          Neu
        </button>
      </div>
    </header>

    <section class="card">
      <!-- Filter -->
      <div class="form mb-3">
        <div class="field">
          <label for="f-location">Ort</label>
          <select
            id="f-location"
            v-model="filters.location"
          >
            <option value="">
              Alle
            </option>
            <option
              v-for="loc in uniqueLocations"
              :key="loc"
              :value="loc"
            >
              {{ loc }}
            </option>
          </select>
        </div>

        <div class="field">
          <label for="f-teacher">Verantwortlich</label>
          <select
            id="f-teacher"
            v-model="filters.teacher"
          >
            <option value="">
              Alle
            </option>
            <option
              v-for="t in teacherOptions"
              :key="t.id"
              :value="String(t.id)"
            >
              {{ t.name }}
            </option>
          </select>
        </div>

        <div class="field">
          <label for="f-mandatory">Pflicht</label>
          <select
            id="f-mandatory"
            v-model="filters.mandatory"
          >
            <option value="">
              Alle
            </option>
            <option value="yes">
              Ja
            </option>
            <option value="no">
              Nein
            </option>
          </select>
        </div>
        <div class="field">
          <label for="f-status">Status</label>
          <select
            id="f-status"
            v-model="filters.status"
          >
            <option value="">
              alle
            </option>
            <option value="active">
              aktiv
            </option>
            <option value="paused">
              pausiert
            </option>
            <option value="expired">
              ausgelaufen
            </option>
          </select>
        </div>
        <div class="field">
          <label for="f-cycle">Zyklus</label>
          <select
            id="f-cycle"
            v-model="filters.cycle"
          >
            <option value="">
              alle
            </option>
            <option
              v-for="c in uniqueCycles"
              :key="c"
              :value="c"
            >
              {{ mapCycleLabel(c) }}
            </option>
          </select>
        </div>
        <div
          class="field text-right"
          style="margin-left:auto;"
        >
          <label
            for="f-reset"
            aria-hidden="true"
            style="visibility:hidden;"
          >Reset</label>
          <button
            id="f-reset"
            class="btn btn-primary"
            :disabled="!hasActiveFilters"
            @click="resetFilters"
          >
            Reset
          </button>
        </div>
      </div>

      <!-- Tabelle -->
      <div class="table">
        <div class="table-header">
          <div>Angebot</div>
          <div>Ort</div>
          <div>Verantwortlich</div>
          <div class="text-center">
            Pflicht
          </div>
          <div class="text-center">
            Schüler
          </div>
          <div class="text-center">
            Zyklus
          </div>
          <div class="text-center">
            Zeit
          </div>
          <div class="text-center">
            Status
          </div>
          <div class="text-center">
            Folgetermin
          </div>
          <div class="text-right">
            Aktion
          </div>
        </div>

        <div
          v-for="row in paged"
          :key="row.id"
          class="table-row"
          :class="row.status === 'expired' ? 'is-expired' : ''"
          :aria-label="row.status === 'expired' ? 'Automatik ausgelaufen' : null"
        >
          <div><strong>{{ row.subject || row.name || '—' }}</strong></div>
          <div class="muted">
            {{ row.location || '—' }}
          </div>
          <div class="muted">
            {{ fmtTeacherName(row) }}
          </div>
          <div class="text-center">
            <span :class="['pill', isMandatory(row) ? 'pill-mandatory' : 'pill-optional']">
              {{ isMandatory(row) ? 'Ja' : 'Nein' }}
            </span>
          </div>
          <div class="text-center">
            {{ row.participant_count ?? 0 }}
          </div>
          <div class="muted text-center">
            {{ formatCycleForRow(row) }}
          </div>
          <div class="muted text-center">
            {{ fmtTime(row.start_at) }} – {{ fmtTime(row.ends_at) }}
          </div>
          <div class="text-center muted">
            {{ mapStatus(row.status, row) }}
          </div>
          <div class="muted text-center">
            {{ row.status !== 'active' ? '—' : fmtDate(row.next_occurrence || row.next_date) }}
          </div>
          <div class="row-actions">
            <button
              class="icon-btn"
              title="Schüler zuweisen"
              aria-label="Schüler zuweisen"
              @click="openAssign(row)"
            >
              <i
                class="pi pi-users"
                aria-hidden="true"
              />
            </button>

            <button
              class="icon-btn"
              :title="'Bearbeiten'"
              aria-label="Bearbeiten"
              @click="openEdit(row)"
            >
              <i
                class="pi pi-pencil"
                aria-hidden="true"
              />
            </button>
            <button
              class="icon-btn"
              :class="row.status==='active' ? '' : 'success'"
              :title="row.status==='active' ? 'Pausieren' : 'Starten'"
              :aria-label="row.status==='active' ? 'Pausieren' : 'Starten'"
              @click="toggle(row)"
            >
              <i
                :class="['pi', row.status==='active' ? 'pi-pause' : 'pi-play']"
                aria-hidden="true"
              />
            </button>
            <button
              class="icon-btn danger"
              :title="'Löschen'"
              aria-label="Löschen"
              @click="confirmDelete(row)"
            >
              <i
                class="pi pi-trash"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>

        <p
          v-if="!loading && filtered.length === 0"
          class="muted"
        >
          Keine Einträge.
        </p>
        <p
          v-if="error"
          class="notice notice-danger mt-12"
          role="alert"
        >
          {{ error }}
        </p>
      </div>

      <!-- Pagination -->
      <div
        class="mt-12"
        style="display:flex;align-items:center;gap:8px;justify-content:flex-end;"
      >
        <span
          class="muted"
          style="margin-right:auto"
        >Gesamt: {{ realTotal }}</span>
        <label
          class="muted"
          for="aor-limit"
        >pro Seite:</label>
        <select
          id="aor-limit"
          :value="limit"
          @change="changeLimit"
        >
          <option
            v-for="n in [10,20,50,100]"
            :key="n"
            :value="n"
          >
            {{ n }}
          </option>
        </select>
        <button
          class="btn"
          :disabled="page<=1"
          @click="prevPage"
        >
          Zurück
        </button>
        <span class="muted">Seite {{ page }} / {{ pages }}</span>
        <button
          class="btn"
          :disabled="page>=pages"
          @click="nextPage"
        >
          Weiter
        </button>
      </div>
    </section>

    <!-- Dialoge -->
    <DeleteConfirmDialog
      :open="!!toDelete"
      :busy="loading"
      :test-id="'auto-offer-delete-dialog'"
      @close="toDelete=null"
      @confirm="doDelete"
    />

    <AutoOfferRuleEditDialog
      v-model="editOpen"
      :rule="editItem"
      @saved="onSaved"
      @toast="onDialogToast"
    />

    <OfferAssignDialog
      :open="assignOpen"
      :offer="assignOffer"
      mode="autoOffer"
      @close="assignOpen=false"
      @applied="load"
    />

    <teleport to=".top-nav-help-slot">
      <span
        id="top-nav-help-offers"
        class="top-nav-help"
      >
        <UiInfoButton
          title="Hilfe: Angebote"
          doc-id="angebote_automatik"
        >
          <ul>
            <li>Filter: Datum (heute vorausgewählt), Suche (Angebot/Thema), Ort, Verantwortlich, Pflicht und Status.</li>
            <li>Sortieren: Spalten „Zeit“, „Ort“ und „Verantwortlich“ sind klickbar (▲/▼ zeigt die Richtung).</li>
            <li>Aktionen je Status (geplant/bereit/gestartet/abgesagt/abgeschlossen) sind rechts in jeder Zeile verfügbar.</li>
            <li>Konflikte gleicher Lehrkraft werden in der Liste rot hinterlegt; Hinweis via Tooltip im Titel.</li>
            <li>Teilnehmerzahl-Button öffnet die Anwesenheiten (startet ggf. automatisch aus „bereit“).</li>
            <li>Paginierung unten: Gesamtanzahl, „pro Seite“, sowie Navigation zwischen den Seiten.</li>
            <li>„Neu“-Button im Kopfbereich legt ein neues Angebot an.</li>
          </ul>
        </UiInfoButton>
      </span>
    </teleport>
  </main>
</template>

<script setup>
import {ref, computed, onMounted, watch} from 'vue';
import {useToast} from 'primevue/usetoast';
import DeleteConfirmDialog from '@/components/dialogs/DeleteConfirmDialog.vue';
import AutoOfferRuleEditDialog from '@/components/autooffers/AutoOfferRuleEditDialog.vue';
import OfferAssignDialog from '@/components/OfferAssignDialog.vue';
import autoOffersApi from '@/api/autoOffers';
import api from '@/services/api';
import UiInfoButton from "@/components/UiInfoButton.vue";

const toast = useToast();

const loading = ref(false);
const error = ref(null);
const items = ref([]);

// Lehrer fürs Anzeigen/Filtern nachladen (sonst sieht man nur teacher_id)
const teacherOptions = ref([]);

const teacherById = computed(() => {
  const map = {};
  for (const t of (teacherOptions.value || [])) {
    map[String(t.id)] = t.name;
  }
  return map;
});

const filters = ref({
  location: '',
  teacher: '',
  mandatory: '',
  status: '',
  cycle: ''
});

const page = ref(1);
const limit = ref(20);

const editOpen = ref(false);
const editItem = ref(null);
const toDelete = ref(null);

const assignOpen = ref(false);
const assignOffer = ref(null);

function mapStatus(s) {
  if (s === 'expired') return 'ausgelaufen';
  if (s === 'paused') return 'pausiert';
  if (s === 'active') return 'aktiv';
  return s || '—';
}

function mapCycleLabel(key) {
  const m = {
    daily: 'täglich (Mo–Fr)',
    weekly: 'wöchentlich',
    biweekly: '2‑wöchentlich',
    triweekly: '3‑wöchentlich',
    monthly: 'monatlich'
  };
  return m[key] || key || '—';
}

function onDialogToast(payload) {
  const severity = payload?.severity || 'info';
  const summary = payload?.summary || 'Aktion ausgeführt.';
  toast.add({severity, summary, life: 3500});
}

function mapWeekDayShort(val) {
  const n = Number(val);
  if (!Number.isFinite(n)) return '';
  // Primär: 0–6 (So–Sa)
  if (n >= 0 && n <= 6) {
    const days0 = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    return days0[n];
  }
  // Fallback: 1–7 (Mo–So), für evtl. ältere Daten
  if (n >= 1 && n <= 7) {
    const days1 = [null, 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
    return days1[n] || '';
  }
  return '';
}

function formatCycleForRow(row) {
  const key = getCycleKey(row);
  let label = mapCycleLabel(key);
  if (key !== 'daily') {
    const wd = mapWeekDayShort(row?.week_day ?? row?.weekday ?? row?.weekDay ?? '');
    if (wd) {
      label += ` (${wd})`;
    }
  }
  return label;
}

function getCycleKey(row) {
  return String(row?.rule || '').trim();
}

function isMandatory(row) {
  return Boolean(row?.is_mandatory ?? row?.mandatory ?? false);
}

function fmtTeacherName(row) {
  const id = row?.teacher_id;
  const key = String(id ?? '').trim();
  if (!key) return '—';
  return teacherById.value[key] || key; // Fallback: ID anzeigen, falls Name nicht geladen
}

function fmtTime(val) {
  if (!val) return '—';
  try {
    const d = new Date(val);
    if (!Number.isNaN(d.getTime())) {
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${hh}:${mm}`;
    }
  } catch (e) {
    // ignore
  }
  const m = String(val).match(/(\d{1,2}):(\d{2})/);
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : String(val);
}

function fmtDate(val) {
  if (!val) return '—';
  try {
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return '—';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  } catch (e) {
    return '—';
  }
}

const uniqueLocations = computed(() =>
    Array.from(new Set((items.value || []).map(r => r.location).filter(Boolean)))
        .sort((a, b) => String(a).localeCompare(String(b), 'de', {sensitivity: 'base'}))
);

const uniqueCycles = computed(() =>
    Array.from(new Set((items.value || []).map(r => getCycleKey(r)).filter(Boolean)))
);

const hasActiveFilters = computed(() =>
    Boolean(filters.value.location || filters.value.teacher || filters.value.mandatory || filters.value.status || filters.value.cycle)
);

const filtered = computed(() => {
  let base = items.value || [];
  if (filters.value.location) {
    base = base.filter(r => (r.location || '') === filters.value.location);
  }
  if (filters.value.teacher) {
    base = base.filter(r => String(r.teacher_id ?? '') === String(filters.value.teacher));
  }
  if (filters.value.mandatory) {
    base = base.filter(r => (filters.value.mandatory === 'yes' ? isMandatory(r) : !isMandatory(r)));
  }
  if (filters.value.status) {
    base = base.filter(r => (r.status || '') === filters.value.status);
  }
  if (filters.value.cycle) {
    base = base.filter(r => getCycleKey(r) === filters.value.cycle);
  }
  return base;
});

const realTotal = computed(() => (items.value || []).length);
const total = computed(() => filtered.value.length);
const pages = computed(() => Math.max(1, Math.ceil(total.value / limit.value)));
const paged = computed(() => {
  const start = (page.value - 1) * limit.value;
  return filtered.value.slice(start, start + limit.value);
});

watch([total, limit], () => {
  if (page.value > pages.value) page.value = pages.value;
  if (page.value < 1) page.value = 1;
});

function nextPage() {
  if (page.value < pages.value) page.value++;
}

function prevPage() {
  if (page.value > 1) page.value--;
}

function changeLimit(e) {
  limit.value = Number(e.target.value);
  page.value = 1;
}

function resetFilters() {
  filters.value = {location: '', teacher: '', mandatory: '', status: '', cycle: ''};
  load();
}

async function loadTeachers() {
  try {
    const res = await api.get('/users/assignable', {params: {roles: '2'}});
    const raw = res?.data?.data ?? res?.data ?? [];
    const list = Array.isArray(raw) ? raw : [];
    teacherOptions.value = list
        .map(u => ({
          id: Number(u.id ?? 0),
          name: String(u.displayName ?? u.name ?? '').trim()
        }))
        .filter(u => Number.isFinite(u.id) && u.id > 0 && u.name);
  } catch (e) {
    teacherOptions.value = [];
  }
}

async function load() {
  loading.value = true;
  error.value = null;
  try {
    const {data} = await autoOffersApi.list({});
    items.value = Array.isArray(data) ? data : (data?.items || []);
    page.value = 1;
  } catch (e) {
    error.value = 'Laden fehlgeschlagen.';
    toast.add({severity: 'error', summary: 'Laden fehlgeschlagen.', life: 3500});
  } finally {
    loading.value = false;
  }
}

function openEdit(row) {
  editItem.value = row;
  editOpen.value = true;
}

async function toggle(row) {
  try {
    if (row.status === 'active') {
      await autoOffersApi.pause(row.id);
      toast.add({severity: 'success', summary: 'Pausiert.', life: 3000});
    } else {
      await autoOffersApi.start(row.id);
      toast.add({severity: 'success', summary: 'Gestartet.', life: 3000});
    }
    await load();
  } catch (e) {
    toast.add({severity: 'error', summary: 'Aktion fehlgeschlagen.', life: 3500});
  }
}

function confirmDelete(row) {
  toDelete.value = row;
}

async function doDelete() {
  if (!toDelete.value) return;
  loading.value = true;
  error.value = null;
  try {
    await autoOffersApi.remove(toDelete.value.id);
    toDelete.value = null;
    await load();
    toast.add({severity: 'success', summary: 'Gelöscht.', life: 3000});
  } catch (e) {
    toast.add({severity: 'error', summary: 'Löschen fehlgeschlagen.', life: 3500});
  } finally {
    loading.value = false;
  }
}

function onSaved() {
  editOpen.value = false;
  load();
}

function openCreate() {
  editItem.value = null;
  editOpen.value = true;
}

function openAssign(row) {
  assignOffer.value = {
    id: row.id,
    title: row.subject || row.name || `Angebot #${row.id}`,
    topic: row.location || '',
    teacher: fmtTeacherName(row)
  };
  assignOpen.value = true;
}

onMounted(async () => {
  await Promise.all([loadTeachers(), load()]);
});
</script>

<style scoped>
.table-header, .table-row {
  display: grid;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid var(--border);
  padding: 5px 0;
  font-size: 14px;
  grid-template-columns: 1.2fr 1fr 160px 120px 80px 140px 120px 90px 120px 160px;
}

/* Expired */
.table-row.is-expired {
  background: color-mix(in srgb, var(--warn) 12%, transparent);
  border-left: 4px solid var(--warn);
  padding-left: 8px;
}

.page-header {
  border-bottom: 1px solid var(--border);
  padding-bottom: 8px;
  margin-bottom: 12px;
}

.row-actions {
  display: inline-flex;
  gap: 8px;
  justify-content: flex-end;
}

.muted {
  color: var(--muted);
}

.mt-12 {
  margin-top: 12px;
}

.form {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
}

.btn {
  padding: .35rem .6rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
}

.btn-primary {
  background: var(--primary);
  color: #fff;
  border-color: var(--primary);
}

.btn-warn {
  background: var(--warn);
  color: #fff;
  border-color: var(--warn);
}

.pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  border: 1px solid var(--border);
}

.pill-mandatory {
  background: rgba(239, 68, 68, 0.10);
  border-color: #FCA5A5;
  color: var(--text);
}

.pill-optional {
  background: var(--bg);
  color: var(--muted);
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.table {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

@media (max-width: 767px) {
  .table-header, .table-row {
    min-width: 1180px;
  }
}
</style>
